// src/pages/veci/DetalleNegocioVeci.js
//
// Detalle del negocio conectado al backend real:
// - GET /api/holaveci/negocios/{id}                → info del negocio
// - GET /api/holaveci/negocios/{id}/productos      → catálogo real
// El carrito sigue usando el mock del AppContext (W4 lo conecta).

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box, Typography, Stack, Button, Card, CardContent, Grid, Chip, IconButton,
  Divider, TextField, InputAdornment, Paper, Alert, CircularProgress, Skeleton,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon        from '@mui/icons-material/ArrowBack';
import AddIcon              from '@mui/icons-material/Add';
import RemoveIcon           from '@mui/icons-material/Remove';
import SearchIcon           from '@mui/icons-material/Search';
import ShoppingBasketIcon   from '@mui/icons-material/ShoppingBasket';
import ScheduleIcon         from '@mui/icons-material/Schedule';
import CreditScoreIcon      from '@mui/icons-material/CreditScore';
import ChatIcon             from '@mui/icons-material/Chat';

import { useApp } from '../../context/AppContext';
import { negocios as apiNegocios } from '../../api/negociosApi';
import { formatoCOP } from '../../utils/format';

export default function DetalleNegocioVeci() {
  const { negocioId } = useParams();
  const navigate = useNavigate();
  const {
    agregarAlCarrito, cambiarCantidadCarrito, carrito, totalCarrito, cantidadTotalCarrito,
  } = useApp();

  const [negocio,   setNegocio]     = useState(null);
  const [productos, setProductos]   = useState([]);
  const [busqueda,  setBusqueda]    = useState('');
  const [cargando,  setCargando]    = useState(true);
  const [error,     setError]       = useState('');

  const cargar = useCallback(async () => {
    setCargando(true); setError('');
    try {
      const [neg, prods] = await Promise.all([
        apiNegocios.detalle(negocioId),
        apiNegocios.productos(negocioId, { pageSize: 500 }),
      ]);
      setNegocio(neg);
      const list = Array.isArray(prods) ? prods : (prods.items || prods.Items || []);
      setProductos(list);
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudo cargar el negocio.');
    } finally { setCargando(false); }
  }, [negocioId]);

  useEffect(() => { cargar(); }, [cargar]);

  const filtrados = useMemo(() => {
    if (!busqueda.trim()) return productos;
    const q = busqueda.toLowerCase();
    return productos.filter(p => (p.nombre || p.Nombre || '').toLowerCase().includes(q));
  }, [productos, busqueda]);

  // Carrito (aún mock): solo cuenta si es de ESTE negocio
  const esMiCarrito = carrito.negocioId === negocioId;
  const itemsAqui = esMiCarrito ? carrito.items : [];
  const cantidadEnCarrito = (pid) =>
    itemsAqui.find((it) => it.productoId === pid)?.cantidad || 0;
  const unidadesAqui = esMiCarrito ? cantidadTotalCarrito() : 0;
  const totalEnEsteNegocio = esMiCarrito ? totalCarrito() : 0;

  const agregar = (p) => {
    const pid    = p.productoId || p.id;
    const nombre = p.nombre || p.Nombre;
    const precio = p.precioVenta ?? p.PrecioVenta ?? p.precio ?? 0;
    agregarAlCarrito(negocioId, {
      productoId: pid,
      nombre,
      precio,
      cantidad:   1,
    });
  };

  const bajar = (p) => {
    const pid = p.productoId || p.id;
    const actual = cantidadEnCarrito(pid);
    if (actual <= 0) return;
    cambiarCantidadCarrito(pid, actual - 1);
  };

  if (cargando) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="rounded" height={140} />
        <Skeleton variant="rounded" height={60} />
        <Skeleton variant="rounded" height={400} />
      </Stack>
    );
  }

  if (error || !negocio) {
    return (
      <Stack spacing={2}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/veci')}>Volver</Button>
        <Alert severity="error">{error || 'Negocio no encontrado.'}</Alert>
      </Stack>
    );
  }

  const nombre = negocio.nombre || negocio.Nombre;
  const direccion = negocio.direccion || negocio.Direccion;
  const horario = negocio.horario || negocio.Horario;
  const abierto = negocio.abiertoAhora ?? negocio.AbiertoAhora;
  const aceptaFiado = negocio.aceptaFiado ?? negocio.AceptaFiado;
  const compromiso = negocio.minutosCompromiso ?? negocio.MinutosCompromiso;
  const promedio = negocio.minutosRespuestaPromedio ?? negocio.MinutosRespuestaPromedio;
  const color = negocio.color || negocio.Color || '#0F4C3A';
  const categoriaNombre = (negocio.categoria || negocio.Categoria)?.nombre;

  return (
    <Stack spacing={3}>
      {/* Header con volver */}
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/veci')} sx={{ mb: 1 }}>
          Volver al directorio
        </Button>
      </Box>

      {/* Card del negocio */}
      <Card>
        <Box sx={{ bgcolor: color, height: 10 }} />
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 1 }}>
            <Typography variant="h4" sx={{ fontFamily: 'Fraunces' }}>{nombre}</Typography>
            {abierto != null && (
              <Chip label={abierto ? 'Abierto' : 'Cerrado'} color={abierto ? 'success' : 'default'} />
            )}
          </Box>

          {categoriaNombre && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {categoriaNombre}
            </Typography>
          )}

          {direccion && (
            <Typography variant="body2" sx={{ mb: 1 }}>{direccion}</Typography>
          )}

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
            {horario && <Chip size="small" icon={<ScheduleIcon />} label={horario} variant="outlined" />}
            {compromiso && <Chip size="small" label={`Compromiso ~${compromiso} min`} color="info" variant="outlined" />}
            {promedio && <Chip size="small" label={`Responde en ~${promedio} min`} variant="outlined" />}
            {aceptaFiado && <Chip size="small" icon={<CreditScoreIcon />} label="Acepta fiado" color="warning" variant="outlined" />}
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Button
            variant="outlined"
            startIcon={<ChatIcon />}
            onClick={() => navigate(`/veci/mensajes/${negocioId}`)}
          >
            Escribir a la tienda
          </Button>
        </CardContent>
      </Card>

      {/* Búsqueda y catálogo */}
      <TextField
        fullWidth
        placeholder="Buscar en el catálogo…"
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
        }}
        sx={{ bgcolor: 'background.paper' }}
      />

      {filtrados.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              {productos.length === 0
                ? 'Este negocio aún no ha publicado productos.'
                : 'No hay productos que coincidan con tu búsqueda.'}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {filtrados.map((p) => {
            const pid    = p.productoId || p.id;
            const nombre = p.nombre || p.Nombre;
            const precio = p.precioVenta ?? p.PrecioVenta ?? p.precio ?? 0;
            const desc   = p.descripcionCorta || p.DescripcionCorta;
            const cantidad = cantidadEnCarrito(pid);

            return (
              <Grid key={pid} item xs={12} sm={6} md={4}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{nombre}</Typography>
                    {desc && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        {desc}
                      </Typography>
                    )}
                    <Typography variant="h6" sx={{ color: 'primary.main', mt: 1 }}>
                      {formatoCOP(precio)}
                    </Typography>
                  </CardContent>
                  <Box sx={{ p: 2, pt: 0 }}>
                    {cantidad === 0 ? (
                      <Button
                        fullWidth variant="contained" startIcon={<AddIcon />}
                        onClick={() => agregar(p)}
                      >
                        Agregar
                      </Button>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <IconButton onClick={() => bajar(p)} size="small"><RemoveIcon /></IconButton>
                        <Typography variant="h6">{cantidad}</Typography>
                        <IconButton onClick={() => agregar(p)} size="small" color="primary"><AddIcon /></IconButton>
                      </Box>
                    )}
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Barra flotante del carrito */}
      {unidadesAqui > 0 && (
        <Paper elevation={8} sx={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          zIndex: 10, minWidth: 320, borderRadius: 3,
        }}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<ShoppingBasketIcon />}
            onClick={() => navigate('/veci/carrito')}
            sx={{ py: 1.5 }}
          >
            Ver carrito ({unidadesAqui}) — {formatoCOP(totalEnEsteNegocio)}
          </Button>
        </Paper>
      )}
    </Stack>
  );
}
